<template>
    <div v-if="todisplay && !readOnly" v-show="isvisible"
         :style="{...sizeStyle, border: '1px solid var(--v-lightGreyBorder-base)', borderRadius: '8px', xdisplay: 'flex', display: 'flex', flexDirection: 'column' }"
         xclass="d-flex flex-column"
         :class="{ 'c-RoosterEditor': true, [`c-name-${this.name || 'unnamed'}`]: true, ['d-flex']: false }">

        <div
            v-if="preRenderDone"
            :style="roosterTipTapShellStyle"
        >
            <RoosterTipTapEditor
                ref="tiptapRef"
                :model-value="model"
                :read-only="readOnly"
                :enable-formatting="!!enableFormatting"
                :enable-image-picker="!!enableImagePicker"
                :enable-quick-text="enableQuickText"
                :show-send-button="!!showSendButton"
                :send-disabled="sendDisabled"
                @update:model-value="updateContent"
                @send="onEditorSend"
            />
        </div>
        <QBtn v-if="showSendButton" :disable="sendDisabled" icon="mdi-send" color="primary" @click="doSend" class="c-RoosterSendButton">Send</QBtn>
    </div>
    <div v-else v-show="isvisible"
         :style="{...sizeStyle, width: 'fit-content', flexGrow: '0', flexShrink: '0' }"
         :class="{ 'c-RoosterEditor': true, [`c-name-${this.name || 'unnamed'}`]: true, ['d-flex']: false }"
         v-html="model"
         >
    </div>
</template>

<script>
/*
 * Rooster editor props:
 * 
        <QuillEditor v-if="preRenderDone"
                     :readOnly="readOnly"
                     xmax-height="maxHeight"
                     toolbar="full"
                     v-model:content="model"
                     xstyle="height: 100%;"
                     /> 

            :initial-content="source"
            :disable-formatting="!enableFormatting"
            :allow-send="showSendButton"
            :send-on-enter="showSendButton"
            :send-disabled="sendDisabled"
            :disableImagePicker="!enableImagePicker"
            :disableQuickText="!enableQuickText"
            :content-format="'markdown'"   // Optional. Defaults to html.
            :emit-markdown="true"          // Optional. Emits MarkdownText in Send payload.
            @input="sync"
            @send="doSend"

 */
import { defineComponent } from 'vue'
import { Vue } from '@/Shared/vueCompat.js';
import { QBtn } from 'quasar';
import { marked } from 'marked';
import TurndownService from 'turndown';
import './schemaform/formHtmlEditor.css';

import BaseComponent from './BaseComponentMixin.jsx';
import careHelpfulFunctions from '../careHelpfulFunctions.jsx';
import RoosterTipTapEditor from './RoosterTipTapEditor.jsx';

import utils from '../../Shared/utils.jsx';

export default defineComponent({
    name: "rooster-editor",
    mixins: [BaseComponent],
    components: {
        RoosterTipTapEditor,
        QBtn,
    },
    data() {
        return {
            preRenderDone: false,
            readOnlyEval: null,
            maxHeightEval: null,
            enableFormattingEval: null,
            showSendButtonEval: null,
            sendDisabledEval: null,
            setValueFunction: null,
            sourceUrlEval: null,
            sourceRawInterpolatedEval: null,
            model: '',
            thisproxy: null,
            markdownModel: '',
            markdownConverter: null,
        }
    },
    computed: {
        readOnly() {
            try {
                if(this.readOnlyEval === null && this.controlData.Readonly)
                    this.readOnlyEval = utils.compile(this, this.controlData.Readonly);
                if(this.readOnlyEval)
                    return utils.evaluate(this.readOnlyEval, this);
                return false;
            }
            catch (e) {
                utils.warn('ReadOnly could not evaluate expression: ' + this.controlData.Readonly + '; ' + e);
                return false;
            }
        },
        maxHeight() {
            try {
                if(this.maxHeightEval === null && this.controlData.MaxHeight)
                    this.maxHeightEval = utils.compile(this, this.controlData.MaxHeight);
                if(this.maxHeightEval)
                    return utils.evaluate(this.maxHeightEval, this);
                return null;
            }
            catch (e) {
                utils.warn('MaxHeight could not evaluate expression: ' + this.controlData.MaxHeight + '; ' + e);
                return null;
            }
        },
        enableFormatting() {
            try {
                if(this.enableFormattingEval === null && this.controlData.EnableFormatting)
                    this.enableFormattingEval = utils.compile(this, this.controlData.EnableFormatting);
                if(this.enableFormattingEval)
                    return utils.evaluate(this.enableFormattingEval, this);
                return null;
            }
            catch (e) {
                utils.warn('EnableFormatting could not evaluate expression: ' + this.controlData.EnableFormatting + '; ' + e);
                return null;
            }
        },
        enableImagePicker() {
            return this.enableFormatting && this.controlData.EnableImagePicker;
        },
        enableQuickText() {
            return ('EnableQuickText' in this.controlData) ? this.controlData.EnableQuickText : true;
        },
        showSendButton() {
            try {
                if(this.showSendButtonEval === null && this.controlData.ShowSendButton)
                    this.showSendButtonEval = utils.compile(this, this.controlData.ShowSendButton);
                if(this.showSendButtonEval)
                    return utils.evaluate(this.showSendButtonEval, this);
                return null;
            }
            catch (e) {
                utils.warn('ShowSendButton could not evaluate expression: ' + this.controlData.ShowSendButton + '; ' + e);
                return null;
            }
        },
        sendDisabled() {
            if(!this.showSendButton)
                return true;
            try {
                if(this.sendDisabledEval === null && this.controlData.SendIsDisabled)
                    this.sendDisabledEval = utils.compile(this, this.controlData.SendIsDisabled);

                if (this.sendDisabledEval) {
                    const value = utils.evaluate(this.sendDisabledEval, this);
                    if (typeof value === 'string')
                        return value.toLowerCase() == 'true';
                    else
                        return !!value;
                }
                return false;
            }
            catch (e) {
                utils.warn('SendIsDisabled could not evaluate expression: ' + this.controlData.SendIsDisabled + '; ' + e);
                return false;
            }
        },
        contentFormat() {
            const raw = (this.controlData?.ContentFormat || 'html');
            const normalized = String(raw).trim().toLowerCase();
            return normalized === 'markdown' || normalized === 'md' ? 'markdown' : 'html';
        },
        emitMarkdown() {
            return !!this.controlData?.EmitMarkdown;
        },
        publishField: {
            set(value){
                if(this.setValueFunction === null && this.controlData.PublishField) {
                    let vueSet = utils.helpers.convertSetValueToVueSet(this.controlData.PublishField, 'richText');
                    this.setValueFunction = new Function('richText', 'context', 'util', 'vue', `with (context) { ${vueSet} }`);
                    this.thisproxy = utils.helpers.getWithProxy(this);
                }
                if(this.setValueFunction) {
                    this.setValueFunction(value, this.thisproxy.proxy, careHelpfulFunctions, Vue);
                }
                this.model = value;
            },
            get() {
                return this.model;
            }
        },
        sourceUrl() {
            return utils.evaluate(this.sourceUrlEval, this);
        },
        sourceRawInterpolated(){
            return utils.evaluate(this.sourceRawInterpolatedEval, this);
        },
        roosterTipTapShellStyle() {
            const base = {
                overflow: 'auto',
                flexGrow: '1',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
            };
            if (this.maxHeight)
                return { ...base, minHeight: this.maxHeight, height: this.maxHeight };
            return base;
        },
        Text() {
            const t = this.$refs.tiptapRef;
            if (t && typeof t.getPlainText === 'function')
                return t.getPlainText();
            return this.htmlToPlainText(this.model || '');
        },
        HtmlRaw() {
            const t = this.$refs.tiptapRef;
            if (t && typeof t.getHTML === 'function')
                return t.getHTML();
            return this.model || '';
        },
    },
    created() {
        switch (this.controlData.DataType.toLowerCase()) {
            case 'url': {
                this.sourceUrlEval = utils.compile(this, this.controlData.DataURL);
                break;
            }
            case 'raw':
            case 'rawinterpolated': {
                this.sourceRawInterpolatedEval = utils.compile(this, this.controlData.Data);
                break;
            }
        }

        this.markdownConverter = new TurndownService({
            headingStyle: 'atx',
            codeBlockStyle: 'fenced',
            emDelimiter: '*',
        });
    },

    methods: {
        markdownToHtml(markdown) {
            if (!markdown)
                return '';
            try {
                return marked.parse(String(markdown), { async: false, gfm: true, breaks: true });
            }
            catch (e) {
                utils.warn('Markdown to HTML conversion failed: ' + e);
                return String(markdown);
            }
        },
        htmlToMarkdown(html) {
            if (!html)
                return '';
            try {
                if (!this.markdownConverter)
                    this.markdownConverter = new TurndownService();
                return this.markdownConverter.turndown(String(html));
            }
            catch (e) {
                utils.warn('HTML to Markdown conversion failed: ' + e);
                return this.htmlToPlainText(html);
            }
        },
        getPlainTextFromEditor() {
            const t = this.$refs.tiptapRef;
            if (t && typeof t.getPlainText === 'function')
                return t.getPlainText();
            return this.htmlToPlainText(this.model || '');
        },
        getEmitPayload(html, plainText) {
            const htmlOutput = typeof html === 'string' ? html : (this.model || '');
            const plainOutput = typeof plainText === 'string' ? plainText : this.getPlainTextFromEditor();
            const markdownOutput = this.emitMarkdown ? this.htmlToMarkdown(htmlOutput) : '';
            return {
                html: htmlOutput,
                plainText: plainOutput,
                markdown: markdownOutput,
            };
        },
        htmlToPlainText(html) {
            if (!html)
                return '';
            const temp = document.createElement('div');
            temp.innerHTML = html;
            return (temp.textContent || temp.innerText || '').trim();
        },
        async Refresh() {
            let sourceData = '';
            if (this.sourceUrl)
                sourceData = await utils.api.get(this.sourceUrl);
            else if (this.sourceRawInterpolated)
                sourceData = this.sourceRawInterpolated;

            const normalized = (sourceData ?? '').toString();
            this.model = this.contentFormat === 'markdown'
                ? this.markdownToHtml(normalized)
                : normalized;

            const payload = this.getEmitPayload(this.model);
            this.value = payload.plainText;
            this.markdownModel = payload.markdown;
        },
        insertQuickText(item, preferSavedRange = true) {
            void preferSavedRange;
            this.$refs.tiptapRef?.insertQuickTextItem?.(item);
        },
        sync(value, plain) {
            const payload = this.getEmitPayload(value, plain);
            this.value = payload.plainText;
            this.publishField = payload.html;
            this.markdownModel = payload.markdown;
        },
        updateContent(value) {
            this.model = value;
            const payload = this.getEmitPayload(value);
            this.value = payload.plainText;
            this.publishField = payload.html;
            this.markdownModel = payload.markdown;
        },
        async preRenderComplete() {
            await this.Refresh();
            this.preRenderDone = true;
            this.finishRenderHandler(this);
        },
        async doSend(html, text) {
            const payload = this.getEmitPayload(html || this.model, text || this.value);
            const actionPayload = {
                Text: payload.html,
                PlainText: payload.plainText,
            };
            if (this.emitMarkdown)
                actionPayload.MarkdownText = payload.markdown;
            await utils.executeAndCompileAllActions(this.controlData.SendActions, actionPayload, this);
        },
        onEditorSend({ html, plainText }) {
            this.doSend(html, plainText);
        },
        ClearRooster() {
            if (this.$refs.tiptapRef?.clearContent)
                this.$refs.tiptapRef.clearContent();
            else {
                this.model = '';
                this.value = '';
            }
        },
        InsertText(text) {
            this.$refs.tiptapRef?.insertPlainText?.(text || '');
        },
    },
});
</script>
<style>
    .vuetify-pro-tiptap-editor__content {
        flex-grow: 1;
        flex-basis: 0;
    }

    .v-input__control {
        flex-grow: 1;
    }

    .vuetify-pro-tiptap {
        display: flex;
        flex-grow: 1;
    }

</style>